import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import { FC, useEffect, useMemo, useState } from 'react'

// component import
import { Alert } from '../../../common/Alert'
import { CustomModal } from '../../../common/CustomModal'

// others
import { useCreateCarriedForwardInterestMutation, useFetchCarriableVoidedBillsQuery } from '../../../../../generated'
import { CANCEL } from '../../../../constants'
import { formatCurrency } from '../../../../utils'

interface CarryForwardInterestModalProps {
  isOpen: boolean
  billId: number
  clientId: string
  onClose: () => void
  onSuccess: () => void
}

const CustomTable = styled(Table)(({ theme }) => ({
  border: '1px solid #E0E0E0',
  '& .MuiTableCell-root': {
    padding: theme.spacing(1.5),
    borderBottom: '1px solid #E0E0E0'
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[50]
  }
}))

const formatDate = (value?: string | number | null) => (value && dayjs(value).isValid() ? dayjs(value).format('MM/DD/YYYY') : '—')

export const CarryForwardInterestModal: FC<CarryForwardInterestModalProps> = ({
  isOpen,
  billId,
  clientId,
  onClose,
  onSuccess
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const { data, loading } = useFetchCarriableVoidedBillsQuery({
    variables: { fetchCarriableVoidedBillsInput: { billId, clientId } },
    fetchPolicy: 'network-only',
    skip: !billId || !clientId
  })

  const bills = useMemo(() => data?.fetchCarriableVoidedBills?.bills ?? [], [data])

  // Pre-select every carriable bill once loaded — recovering all stranded interest is the norm.
  useEffect(() => {
    setSelectedIds(new Set(bills.map(bill => bill.billId)))
  }, [bills])

  const [carryForward, { loading: submitting }] = useCreateCarriedForwardInterestMutation({
    onCompleted: result => {
      if (result?.createCarriedForwardInterest?.response?.status === 200) {
        Alert.success(result.createCarriedForwardInterest.response.message || 'Interest carried forward successfully')
        onClose()
        onSuccess()
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to carry forward interest')
    }
  })

  const total = useMemo(
    () => bills.filter(bill => selectedIds.has(bill.billId)).reduce((sum, bill) => sum + bill.accruedInterest, 0),
    [bills, selectedIds]
  )

  const toggle = (billId: number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (checked) next.add(billId)
      else next.delete(billId)
      return next
    })
  }

  const handleSubmit = () => {
    const sourceBillIds = [...selectedIds]
    if (sourceBillIds.length === 0) {
      Alert.error('Please select at least one voided bill to carry forward')
      return
    }
    carryForward({ variables: { createCarriedForwardInterestInput: { billId, clientId, sourceBillIds } } })
  }

  const hasBills = bills.length > 0

  return (
    <CustomModal title="Carry Forward Interest" isOpen={isOpen} handleClose={onClose} maxWidth="md">
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress size={28} />
        </Box>
      ) : hasBills ? (
        <>
          <MuiAlert severity="info" sx={{ mb: 2 }}>
            Use this option only when you want to carry forward the interest from a voided bill to a new bill.
            <br />
            Below is a list of voided bills with there accrued interest at the time of voiding. Select the bills you want to carry forward the interest from.

          </MuiAlert>

          <TableContainer sx={{ mb: 3 }}>
            <CustomTable>
              <TableHead>
                <TableRow>
                  <TableCell width="8%"></TableCell>
                  <TableCell width="18%">Voided Invoice #</TableCell>
                  <TableCell width="18%">Voided On</TableCell>
                  <TableCell width="30%">Interest Period</TableCell>
                  <TableCell width="26%" align="right">
                    Accrued Interest
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {bills.map(bill => (
                  <TableRow key={bill.billId}>
                    <TableCell>
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(bill.billId)}
                        onChange={event => toggle(bill.billId, event.target.checked)}
                      />
                    </TableCell>
                    <TableCell>#{bill.billId}</TableCell>
                    <TableCell>{formatDate(bill.voidedAt)}</TableCell>
                    <TableCell>
                      {formatDate(bill.fromDate)} – {formatDate(bill.toDate)}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(bill.accruedInterest)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </CustomTable>
          </TableContainer>

          <DialogActions sx={{ padding: '10px 0 0' }}>
            <Button onClick={onClose} disabled={submitting}>
              {CANCEL}
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || selectedIds.size === 0 || total <= 0}
              endIcon={submitting && <CircularProgress size={20} color="inherit" />}
            >
              Add Interest {formatCurrency(total)}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <MuiAlert severity="warning" sx={{ mb: 2 }}>
            There is no recently voided bill with accrued interest for this client. This option is only for carrying
            interest from a bill that was voided and then regenerated — there is nothing to carry forward here.
          </MuiAlert>
          <Typography variant="body2" color="text.secondary">
            If you voided a bill but the interest is not showing up here, it may already have been carried forward, or
            the voided bill never accrued any interest.
          </Typography>

          <DialogActions sx={{ padding: '20px 0 0' }}>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </CustomModal>
  )
}
